// „Mój tydzień” (patch-32): siedem kapsuł Pn–Nd; w każdej buźka nastroju z tego dnia na wysokości zależnej od samopoczucia (od smutnego na dole do radosnego u góry),
// kolor kapsuły to kolor wycinka koła. Pusty dzień = pusta kapsuła, kropka pod literą = tego dnia zrobiono lekcję. Strzałki ‹ › przeglądają tygodnie, ale tylko
// od najwcześniejszego zapisanego nastroju do dziś (wcześniej dziecko nie miało szansy nic zapisać — strzałka wstecz jest tam zablokowana).
// Strzałka powrotu i strzałki tygodni są w pasku u góry, poza białą kartą — jak na ekranie lekcji.
// Napisy idą za przełącznikiem EN/PL (jak nazwy nastrojów); polskie nazwy mają końcówki wg płci dziecka.
import type { Pair } from '../lib/i18n.ts';
import { config } from '../config.ts';
import { moodCollectingDays, moodImage, moodInfo, moodName, oldestWeekOffset, topMood, weekView } from '../lib/mood.ts';
import type { WeekDay } from '../lib/mood.ts';
import type { TeacherLang } from '../types.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import { genderOf } from './forms.ts';
import { langOf, rerenderOnLang, tr, tx, txAttr } from './i18n.ts';
import { icon } from './kit.ts';
import { renderLangToggle } from './langToggle.ts';
import { renderProfileBar } from './profileBar.ts';

const T = {
  title: { pl: 'Mój tydzień', en: 'My week' },
  sub: { pl: 'Jak się czuł{eś|aś} w tym tygodniu', en: 'How you felt this week' },
  cardLabel: { pl: 'Mój tydzień — nastroje', en: 'My week — moods' },
  back: { pl: 'Wróć', en: 'Back' },
  prev: { pl: 'Wcześniejszy tydzień', en: 'Previous week' },
  next: { pl: 'Następny tydzień', en: 'Next week' },
  top: { pl: 'Najczęściej:', en: 'Most often:' },
  emptyNow: { pl: 'Nastrój wybierzesz przed lekcją — wtedy pojawi się tutaj.', en: 'Pick your mood before the lesson — it will show up here.' },
  emptyPast: { pl: 'W tym tygodniu nie zapisano żadnego nastroju.', en: 'No mood was saved this week.' },
  none: { pl: 'brak nastroju', en: 'no mood' },
} as const satisfies Record<string, Pair>;

const MONTHS: Record<TeacherLang, readonly string[]> = {
  pl: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};
const DAY_LABELS: Record<TeacherLang, readonly string[]> = {
  pl: ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
};
const DAY_NAMES: Record<TeacherLang, readonly string[]> = {
  pl: ['poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota', 'niedziela'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

const dm = (iso: string, lang: TeacherLang): { d: number; m: string } => ({ d: Number(iso.slice(8, 10)), m: MONTHS[lang][Number(iso.slice(5, 7)) - 1] ?? '' });

/** „12–18 paź” / „Oct 12–18”, a gdy tydzień przechodzi przez granicę miesiąca: „30 wrz – 6 paź” / „Sep 30 – Oct 6”. */
export function weekRange(days: readonly WeekDay[], lang: TeacherLang = 'pl'): string {
  const a = dm(days[0]!.date, lang);
  const b = dm(days[days.length - 1]!.date, lang);
  if (lang === 'en') return a.m === b.m ? `${b.m} ${a.d}–${b.d}` : `${a.m} ${a.d} – ${b.m} ${b.d}`;
  return a.m === b.m ? `${a.d}–${b.d} ${b.m}` : `${a.d} ${a.m} – ${b.d} ${b.m}`;
}

/** Wysokość buźki w kapsule (0–1) dla poziomu samopoczucia 1–5. */
export const levelFraction = (level: number): number => 0.16 + ((level - 1) / 4) * 0.68;

function capsule(ctx: AppCtx, day: WeekDay, idx: number, lang: TeacherLang): HTMLElement {
  const info = day.entry ? moodInfo(day.entry.mood) : null;
  const f = info ? levelFraction(info.level) : 0;
  const bar = h('div', { class: 'cap-bar', style: `--c:${info?.color ?? '#cfcbe8'};--f:${f.toFixed(3)}` },
    info ? h('div', { class: 'cap-fill' }) : null,
    info ? h('img', { class: 'cap-face', src: moodImage(info.id), alt: '', draggable: 'false', 'data-testid': 'cap-face' }) : null,
  );
  const label = `${DAY_NAMES[lang][idx] ?? ''}${info ? `: ${moodName(info, lang, genderOf(ctx))}` : day.future ? '' : `: ${tr(ctx, T.none)}`}`;
  return h('div', {
    class: `cap${day.today ? ' today' : ''}${day.future ? ' future' : ''}${info ? '' : ' empty'}`,
    role: 'img',
    'aria-label': label,
    'data-date': day.date,
    'data-mood': day.entry?.mood ?? '',
    'data-testid': `week-day-${day.date}`,
  }, bar, h('span', { class: 'cap-label' }, DAY_LABELS[lang][idx] ?? day.label), h('span', { class: `cap-dot${day.lesson ? ' on' : ''}`, 'data-testid': day.lesson ? 'cap-lesson' : undefined, 'aria-hidden': 'true' }));
}

export function renderWeek(ctx: AppCtx, requested = 0): void {
  ctx.leaveHome();
  const progress = ctx.getProgress();
  const oldest = oldestWeekOffset(progress, ctx.today());
  const offset = Math.max(oldest, Math.min(0, requested));
  ctx.at?.({ screen: 'week', offset }); // odświeżenie strony wraca na ten sam tydzień
  const days = weekView(progress, ctx.today(), offset);
  const lang = langOf(ctx);
  const top = topMood(days);

  const back = button(icon('back'), () => ctx.goHome(), { class: 'icon-btn', title: tr(ctx, T.back), testid: 'week-back' });
  back.setAttribute('aria-label', tr(ctx, T.back));
  const prev = button(icon('back'), () => renderWeek(ctx, offset - 1), { class: 'icon-btn small', title: tr(ctx, T.prev), testid: 'week-prev', disabled: offset <= oldest });
  prev.setAttribute('aria-label', tr(ctx, T.prev));
  const next = button(icon('arrow'), () => renderWeek(ctx, offset + 1), { class: 'icon-btn small', title: tr(ctx, T.next), testid: 'week-next', disabled: offset >= 0 });
  next.setAttribute('aria-label', tr(ctx, T.next));

  // „Najczęściej” ma sens dopiero po tygodniu zbierania buziek (od pierwszego zapisanego nastroju) — z jednego dnia nic nie wynika. W czasie zbierania nie ma tu żadnego napisu
  // (decyzja z 19.10, 12:21: dziecko nie potrzebuje informacji „dzień N z 7”, wystarczy, że po tygodniu pojawi się buźka); pusty wiersz zachowuje wysokość karty,
  // więc podsumowanie nie przesuwa wykresu, gdy się pojawi.
  const collecting = moodCollectingDays(progress, ctx.today());
  const needed = config.ui.moodSummaryMinDays;
  const summaryReady = collecting >= needed;
  const note = collecting > 0 && !summaryReady
    ? h('p', { class: 'week-note reserved', 'data-testid': 'week-note', 'data-collecting': collecting, 'aria-hidden': 'true' }, '\u00a0')
    : top
    ? h('p', { class: 'week-note', 'data-testid': 'week-note', 'data-top': top }, `${tr(ctx, T.top)} `, h('img', { class: 'week-note-face', src: moodImage(top), alt: '' }), h('strong', null, moodName(moodInfo(top), lang, genderOf(ctx))))
    : h('p', { class: 'week-note muted', 'data-testid': 'week-note' }, tr(ctx, offset === 0 ? T.emptyNow : T.emptyPast));

  // pasek u góry, poza białą kartą (jak na ekranie lekcji): ← | profil | ‹ › EN
  const topbar = h('header', { class: 'topbar mw-topbar', 'data-testid': 'week-topbar' },
    h('div', { class: 'tb-left' }, back),
    h('div', { class: 'tb-mid' }, renderProfileBar(ctx)),
    h('div', { class: 'tb-right' }, h('div', { class: 'week-nav' }, prev, next), renderLangToggle(ctx)),
  );
  const main = h('main', { class: 'stage week', 'data-testid': 'week-screen', 'data-offset': offset, 'data-oldest': oldest },
    h('div', { class: 'week-card-big' },
      h('div', { class: 'week-heading' }, h('h1', { class: 'week-title' }, tr(ctx, T.title)), h('p', { class: 'week-range', 'data-testid': 'week-range' }, weekRange(days, lang))),
      h('div', { class: 'week-chart', 'data-testid': 'week-chart' }, ...days.map((d, i) => capsule(ctx, d, i, lang))),
      note,
    ),
  );
  ctx.root.replaceChildren(topbar, main);
  // przełącznik EN/PL na ekranie przepisuje napisy od razu (ekran nie ma własnego stanu poza tygodniem, więc rysujemy go od nowa)
  rerenderOnLang(ctx, main, () => renderWeek(ctx, offset));
}

/** Kafelek na ekranie głównym: siedem miniaturek tygodnia i „Mój tydzień ›”; dotknięcie otwiera pełny ekran. Napisy przepisują się po zmianie EN/PL. */
export function renderWeekCard(ctx: AppCtx): HTMLElement {
  const days = weekView(ctx.getProgress(), ctx.today(), 0);
  const minis = days.map((d) => {
    const info = d.entry ? moodInfo(d.entry.mood) : null;
    return h('span', { class: `mini${d.today ? ' today' : ''}${info ? '' : ' empty'}`, 'data-mood': info?.id ?? '' }, info ? h('img', { src: moodImage(info.id), alt: '', draggable: 'false' }) : null);
  });
  const card = h('button', { type: 'button', class: 'week-card', 'data-testid': 'open-week', onclick: () => renderWeek(ctx) },
    h('span', { class: 'wc-text' }, tx(ctx, T.title, 'wc-title'), tx(ctx, T.sub, 'wc-sub')),
    h('span', { class: 'wc-minis' }, ...minis),
    icon('right'),
  );
  return txAttr(ctx, card, 'aria-label', T.cardLabel);
}
